import { cx } from '@linaria/core';
import { useRef, useState, useEffect } from 'react';
import { polyfill } from 'smoothscroll-polyfill';
import { Pm2Icon } from '@pm2/react-utils';
import { pm2SitemapStore, findNavigationItems, findPageDescriptorById, findActiveRootPage, hasMegaMenu } from '@pm2/sitemap-utils';
import { pm2InterfaceStore, pm2ConfigStore } from '@pm2/store';
import { useLocation, Link } from '@provider/navigation';
import { breakpoints } from '@ui/css';
import { printHtmlToJsx, simpleHtmlParsers } from '@utils/ast';
import { isPlatformNodeJS, throttleToAnimationFrame } from '@utils/common';
import { HideFor, Chevron, useLoaderState } from '@utils/react';
import { useOpenMegaMenu } from './MegaMenu/hook.open-mega-menu.js';
import { navCls, scrollChevronCls, navListCls, listItemCls, linkCls, itemIconCls, stateWrapperCls, throbberWrapperCls, throbberCls, chevronCls } from './Navigation.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

if (!isPlatformNodeJS()) {
  polyfill();
}
function Navigation({
  isToolsMounted
}) {
  const location = useLocation();
  const navRef = useRef(null);
  const sitemapState = pm2SitemapStore.useSelector(state => state);
  const items = findNavigationItems(sitemapState);
  const megaMenu = pm2InterfaceStore.useSelector(state => state.megaMenu)?.map(id => findPageDescriptorById(sitemapState, id)).filter(it => it !== undefined);
  const pendingMegaMenu = pm2InterfaceStore.useSelector(state => state.pendingMegaMenu ? findPageDescriptorById(sitemapState, state.pendingMegaMenu) : undefined);
  const [showLeftScrollChevron, setShowLeftScrollChevron] = useState(false);
  const [showRightScrollChevron, setShowRightScrollChevron] = useState(false);

  // determine whether or not there's overflow inside the navigation to make
  // sure that the scroll chevrons are only displayed when they are actually
  // needed
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) {
      return;
    }
    const toggleOverflowState = () => {
      nav.parentElement?.classList.remove("withOverflow");
      nav.parentElement?.classList.toggle("withOverflow", nav.scrollWidth > nav.clientWidth);
      setShowLeftScrollChevron(nav.scrollLeft > 0);
      setShowRightScrollChevron(nav.scrollLeft < nav.scrollWidth - nav.clientWidth - 1);
    };
    toggleOverflowState();
    setTimeout(toggleOverflowState, 400);
    const handleResize = throttleToAnimationFrame(toggleOverflowState);
    const observer = new ResizeObserver(handleResize);
    observer.observe(nav);
    nav.addEventListener("scroll", handleResize);
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      nav.removeEventListener("scroll", handleResize);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      observer.disconnect();
    };
  }, [isToolsMounted]);
  const scrollLeft = () => {
    if (!navRef.current) {
      return;
    }
    navRef.current.scrollTo({
      left: navRef.current.scrollLeft - navRef.current.clientWidth,
      behavior: "smooth"
    });
  };
  const scrollRight = () => {
    if (!navRef.current) {
      return;
    }
    navRef.current.scrollTo({
      left: navRef.current.scrollLeft + navRef.current.clientWidth,
      behavior: "smooth"
    });
  };

  // identify the root level item containing the currently active page so that
  // we can highlight it
  const activeRootPage = megaMenu?.length ? megaMenu[0] : pendingMegaMenu ?? findActiveRootPage(sitemapState, location.pathname);
  items.findIndex(it => it === activeRootPage);
  return /*#__PURE__*/jsx(HideFor, {
    breakpoint: breakpoints.mobile,
    className: navCls,
    children: /*#__PURE__*/jsxs("nav", {
      children: [/*#__PURE__*/jsx("button", {
        className: cx(scrollChevronCls, "left", showLeftScrollChevron && "visible"),
        onClick: scrollLeft,
        tabIndex: -1,
        children: /*#__PURE__*/jsx(Chevron, {
          size: 11,
          color: "currentColor",
          inversed: true
        })
      }), /*#__PURE__*/jsx("ul", {
        className: navListCls,
        ref: navRef,
        children: items.map(link => /*#__PURE__*/jsx(NavigationLink, {
          link: link,
          activeRootPage: activeRootPage
        }, link.id))
      }), /*#__PURE__*/jsx("button", {
        className: cx(scrollChevronCls, "right", showRightScrollChevron && "visible"),
        onClick: scrollRight,
        tabIndex: -1,
        children: /*#__PURE__*/jsx(Chevron, {
          size: 11,
          color: "currentColor"
        })
      })]
    })
  });
}
function NavigationLink({
  link,
  activeRootPage
}) {
  const openMegaMenu = useOpenMegaMenu("default");
  const audience = pm2ConfigStore.useSelector(state => state.config.theme.targetAudience);
  const hasExpandedMegaMenu = pm2InterfaceStore.useSelector(state => state.megaMenu?.[0] === link.id);
  const [isLoadingMegaMenu, setLoadingMegaMenu] = useState(false);
  const showLoader = useLoaderState(isLoadingMegaMenu);
  const onMegaMenuOpen = async () => {
    setLoadingMegaMenu(true);
    try {
      await openMegaMenu(link, {
        isMobile: false
      });
      setLoadingMegaMenu(false);
    } catch (err) {
      setLoadingMegaMenu(false);
      throw err;
    }
  };
  const enableLinkIcon = audience === "Grade1To3" || audience === "Grade4To6";
  return /*#__PURE__*/jsx("li", {
    className: listItemCls,
    children: hasMegaMenu(link) ? /*#__PURE__*/jsxs("button", {
      className: cx(linkCls, activeRootPage === link && "active", hasExpandedMegaMenu && "expandedMegaMenu"),
      "data-nav-button": true,
      onClick: onMegaMenuOpen,
      children: [enableLinkIcon && link.topNavIcon && /*#__PURE__*/jsx("span", {
        className: itemIconCls,
        children: /*#__PURE__*/jsx(Pm2Icon, {
          ident: link.topNavIcon,
          size: 24,
          strokeWidth: 1.15
        })
      }), printHtmlToJsx(link.title, simpleHtmlParsers), /*#__PURE__*/jsxs("span", {
        className: stateWrapperCls,
        children: [/*#__PURE__*/jsx("span", {
          className: cx(throbberWrapperCls, !showLoader.current && "hidden"),
          children: /*#__PURE__*/jsx("span", {
            className: throbberCls
          })
        }), /*#__PURE__*/jsx("span", {
          className: cx(chevronCls, showLoader.current && "hidden"),
          children: /*#__PURE__*/jsx(Chevron, {
            vertical: true,
            inversed: hasExpandedMegaMenu,
            size: 6,
            lineWidth: 1.5
          })
        })]
      })]
    }) : /*#__PURE__*/jsxs(Link, {
      className: cx(linkCls, activeRootPage === link && "active"),
      href: link.path,
      children: [enableLinkIcon && link.topNavIcon && /*#__PURE__*/jsx("span", {
        className: itemIconCls,
        children: /*#__PURE__*/jsx(Pm2Icon, {
          ident: link.topNavIcon,
          size: 24,
          strokeWidth: 1.15
        })
      }), printHtmlToJsx(link.title, simpleHtmlParsers)]
    })
  }, link.id);
}

export { Navigation };
//# sourceMappingURL=Navigation.js.map
